/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {PagePermissionType} from "../models/page-permission-type";
import {Injectable} from '@angular/core';
import {Permission} from "../models/permission";

class PagePermission {
  formattedName: string;
  canRead: boolean;

  constructor(formattedName: string, hasRead: boolean) {
    this.formattedName = formattedName;
    this.canRead = hasRead;
  }
}

class PagePermissions {
  public page: Map<PagePermissionType, PagePermission> = new Map()
    .set(PagePermissionType.HomePage, new PagePermission("Home Page", false))
    .set(PagePermissionType.LocatePage, new PagePermission("Locate Page", false))
    .set(PagePermissionType.SecurityPage, new PagePermission("Security Page", false))
    .set(PagePermissionType.SettingsPage, new PagePermission("Settings Page", false))
    .set(PagePermissionType.HelpPage, new PagePermission("Help Page", false))
    .set(PagePermissionType.UsersPage, new PagePermission("People Page", false))
    .set(PagePermissionType.PeoplePage, new PagePermission("People Page", false))
    .set(PagePermissionType.RoleManagerPage, new PagePermission("Role Manager", false))
    .set(PagePermissionType.InfrastructurePage, new PagePermission("Infrastructure Page", false))
    .set(PagePermissionType.InfrastructureAntennasPage, new PagePermission("Infrastructure Antennas Page", false))
    .set(PagePermissionType.InfrastructureDriverObjectsPage, new PagePermission("Infrastructure Drivers Page", false))
    .set(PagePermissionType.InfrastructureDevicesPage, new PagePermission("Infrastructure Devices Page", false))
    .set(PagePermissionType.InfrastructureAssetsPage, new PagePermission("Infrastructure Assets Page", false))
    .set(PagePermissionType.InfrastructurePeoplePage, new PagePermission("Infrastructure People Page", false))
    .set(PagePermissionType.InfrastructureReadersPage, new PagePermission("Infrastructure Readers Page", false))
    .set(PagePermissionType.InfrastructureLocationsPage, new PagePermission("Infrastructure Locations Page", false))
    .set(PagePermissionType.InfrastructureServerObjectsPage, new PagePermission("Infrastructure Server Objects Page", false))
    .set(PagePermissionType.InfrastructureServicesPage, new PagePermission("Infrastructure Services Page", false))
    .set(PagePermissionType.InfrastructureZonesPage, new PagePermission("Infrastructure Zones Page", false))
    .set(PagePermissionType.InfrastructureTagsPage, new PagePermission("Infrastructure Tags Page", false))
    .set(PagePermissionType.TriggersPage, new PagePermission("Triggers Page", false))
    .set(PagePermissionType.ReactionsPage, new PagePermission("Reactions Page", false))
    .set(PagePermissionType.HealthMonitorPage, new PagePermission("Health Monitor Page", false))
    .set(PagePermissionType.AssetsPage, new PagePermission("Assets Page", false))
    .set(PagePermissionType.RulesPage, new PagePermission("Rules Page", false))
    .set(PagePermissionType.LicensingPage, new PagePermission("Licensing Page", false))
    .set(PagePermissionType.SitesPage, new PagePermission("Sites Page", false))
    .set(PagePermissionType.ReportsPage, new PagePermission("Reports Page", false))
    .set(PagePermissionType.HistoryPage, new PagePermission("History Page", false))
    .set(PagePermissionType.CustomFieldsPage, new PagePermission("Custom Fields Page", true))
    .set(PagePermissionType.InfrastructureRfidEncodingSchemesPage, new PagePermission("Infrastructure RFID Encoding Schemes Page", false))
    .set(PagePermissionType.MonitorAssetsPage, new PagePermission("Monitor Assets Page", false))
    .set(PagePermissionType.MonitorLocationPage, new PagePermission("Monitor Location Page", false))
    .set(PagePermissionType.MonitorTagsPage, new PagePermission("Monitor Tags Page", false))
    .set(PagePermissionType.MonitorZonePage, new PagePermission("Monitor Zone Page", false))
    .set(PagePermissionType.WorkflowsPage, new PagePermission("Workflows Page", false))
    .set(PagePermissionType.SetMmcAffiliatesPage, new PagePermission("Set MMC Affiliates", false))
    .set(PagePermissionType.KissPrintPage, new PagePermission("KISS Print Page", false))
    .set(PagePermissionType.KissPortalValidationPage, new PagePermission("KISS Portal Validation Page", false))
    .set(PagePermissionType.KissOutboundValidationPage, new PagePermission("KISS Outbound Validation Page", false))
    .set(PagePermissionType.DemoOrderPortalValidationPage, new PagePermission("Order Portal Validation Pages", false))
    .set(PagePermissionType.DenverMattressPortalValidationPage, new PagePermission("Denver Mattress Portal Validation Page", false))
    .set(PagePermissionType.BuchananUploadProductPage, new PagePermission("Buchanan: Product Upload Page", false))
    .set(PagePermissionType.BuchananBulkPrintPage, new PagePermission("Buchanan: Bulk Print Page", false))
    .set(PagePermissionType.BuchananPrintPage, new PagePermission("Buchanan: Print Page", false))
    .set(PagePermissionType.ONeilCartonPortalPages, new PagePermission("O'Neil: Carton Portal Validation Pages", false))
    .set(PagePermissionType.GtinValidationSetsPage, new PagePermission("GTIN Validation Sets", false))
    .set(PagePermissionType.ApiTokenPage, new PagePermission("Api Token Page", false))
    .set(PagePermissionType.CartonAuditPortalPage, new PagePermission("Carton Audit Portal Pages", false))
    .set(PagePermissionType.AbleNetShipmentValidationPage, new PagePermission("Ablenet Shipment Validation Page", false))
    .set(PagePermissionType.AbleNetReturnDevicesValidationPage, new PagePermission("Ablenet Return Devices Validation Page", false))
    .set(PagePermissionType.DataMappingPage, new PagePermission("Data Mapping  Page", false))
    .set(PagePermissionType.DataMappingPageUpload, new PagePermission("Data Mapping CSV Upload Page", false))
    .set(PagePermissionType.SwaggerApiPage, new PagePermission("Swagger Documentation", false))
    .set(PagePermissionType.ZoneAssignedAssetsPage, new PagePermission("Zone Assigned Assets", false));


  constructor() {
  }

  public get(pagePermissionType: PagePermissionType): PagePermission {
    return this.page.get(pagePermissionType);
  }

  public canRead(pagePermissionType: PagePermissionType): boolean {
    const result = this.page.get(pagePermissionType);

    if (result == null) {
      console.error("Requested viewing of non-existent page in PagePermissions:" + pagePermissionType);
      return false;
    } else {
      return result.canRead;
    }
  }

  public formattedName(pagePermissionType: PagePermissionType): string {
    const pagePermission = this.page.get(pagePermissionType);

    if (pagePermission != null) {
      return pagePermission.formattedName
    } else {
      console.log("Could not find pagePermission " + pagePermissionType);
      return '';
    }
  }

}

@Injectable()
export class PagePermissionService {


  public cachedPagePermissions: PagePermissions = null;

  constructor() { }

  public refreshCachedPagePermissions(pagePermissions: {}): void {
    this.cachedPagePermissions = new PagePermissions();

    for(const page of Object.keys(PagePermissionType)) {
      const pageObject = this.cachedPagePermissions.get(PagePermissionType[page]);

      if (pageObject != null) {
        pageObject.canRead = this.getReadPagePermission(<PagePermissionType> page, pagePermissions); //this._getReadPagePermission(PagePermissionType.HomePage, pagePermissions);
      } else {
        console.log("Page object for " + page + " is null or undefined");
      }
    }
  }

  private getReadPagePermission(pagePermissionType: PagePermissionType, pagePermissions: {}): boolean {
    if (pagePermissionType == null || pagePermissions == null) {
      return false;
    } else {
      for(const key of Object.keys(PagePermissionType)) {
        if (key == pagePermissionType) {
          if (pagePermissions[key]) {
            return pagePermissions[key].read;
          } else {
            // Default to false if the Page Permission doesn't exist
            return false;
          }
        }
      }
      return false;

    }
  }

  public get(pagePermissionType: PagePermissionType): PagePermission {
    if (this.cachedPagePermissions == null) {
      return null;
    } else {
      return this.cachedPagePermissions.get(pagePermissionType);
    }
  }

  public canRead(pagePermissionType: PagePermissionType): boolean {
    if (this.cachedPagePermissions == null) { return false; }

    return this.cachedPagePermissions.canRead(pagePermissionType);
  }

  public formattedName(pagePermissionType: PagePermissionType): string {
    if (this.cachedPagePermissions == null) { return ""; }

    return this.cachedPagePermissions.formattedName(pagePermissionType);
  }

  public getEmptyPagePermissions(): {[propKey: string]: Permission} {
    // Set up the empty page permissions so a new object that is created can use this for it's starting page permissions

    const emptyPagePermissions: {[propKey: string]: Permission} = {};
    for(const pagePermissionType of Object.keys(PagePermissionType)) {
      emptyPagePermissions[PagePermissionType[pagePermissionType]] = new Permission(false, false, false, false);
    }

    return emptyPagePermissions;

  }

}

/**
 * Represents a "Page" in the Web Application.
 * 
 * Contains the "Permission Type" which is the key used to lookup the Permission on 
 * the page along with a Human Readable "Label" for displaying what the Page is. 
 */
export interface Page {
  /** Page Key for the Page Permission */
  permissionType: PagePermissionType; 

  /** Label for the Page */
  label: string;
} 

